import { useCallback, useState } from 'react';
import { KanameGlyph } from '@/components/brand/KanameGlyph';
import { cn } from './cn';

/*
  Avatar — аватарка. Фото ложится поверх заглушки и проявляется, когда загрузилось; не загрузилось —
  остаётся заглушка. Заглушка: инициалы из name (по умолчанию — для списков людей, где их нужно
  различать) или знак Kaname контуром с пустым ядром (fallback="mark" — только «я»: подвал главного
  меню и окно аккаунта; коралла нет — живое ядро на экране одно, R41). Тон rail — для тёмного меню:
  подложка меню, цвет — от строки. turned — веер заглушки повёрнут на лепесток (отклик на действие;
  знак шестикратно симметричен, поэтому вид тот же).
  Размеры: xs 20 · sm 24 · md 32 · lg 40 · xl 56.
*/

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface AvatarProps {
  src?: string;
  name?: string;
  size?: AvatarSize;
  fallback?: 'initials' | 'mark';
  tone?: 'default' | 'rail';
  turned?: boolean;
  className?: string;
}

const sizeStyles: Record<AvatarSize, string> = {
  xs: 'size-5 text-2xs',
  sm: 'size-6 text-2xs',
  md: 'size-8 text-xs',
  lg: 'size-10 text-sm',
  xl: 'size-14 text-base',
};

/** Знак в круге — около 60 % круга. */
const glyphSizes: Record<AvatarSize, string> = {
  xs: 'size-3',
  sm: 'size-3.5',
  md: 'size-5',
  lg: 'size-6',
  xl: 'size-8',
};

export function Avatar({
  src,
  name,
  size = 'md',
  fallback = 'initials',
  tone = 'default',
  turned = false,
  className,
}: AvatarProps) {
  // Состояние загрузки — по адресу: сменили фото — проявляется заново, а не мелькает пустым кругом.
  const [failedSrc, setFailedSrc] = useState<string>();
  const [loadedSrc, setLoadedSrc] = useState<string>();
  const initials = (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');

  const showImage = !!src && failedSrc !== src;
  const loaded = showImage && loadedSrc === src;
  const covered = loaded;
  // Фото уже в кэше — сразу на месте, без проявления поверх заглушки при каждом показе.
  const imgRef = useCallback(
    (img: HTMLImageElement | null) => {
      if (img?.complete && img.naturalWidth > 0) setLoadedSrc(src);
    },
    [src],
  );

  return (
    <span
      data-slot="avatar"
      role={name ? 'img' : undefined}
      aria-label={name}
      className={cn(
        'relative inline-grid place-items-center overflow-hidden rounded-full border font-medium',
        tone === 'rail' ? 'border-rail-line bg-rail-hover' : 'border-line bg-muted text-fg-muted',
        sizeStyles[size],
        className,
      )}
    >
      {fallback === 'mark' ? (
        <KanameGlyph
          core="ring"
          className={cn(
            glyphSizes[size],
            tone === 'default' && 'text-fg-subtle',
            'transition-transform duration-smooth ease-snappy motion-reduce:transition-none',
            turned && 'rotate-60',
            covered && 'invisible',
          )}
        />
      ) : (
        <span aria-hidden className={cn(covered && 'invisible')}>
          {initials}
        </span>
      )}
      {showImage && (
        <img
          ref={imgRef}
          src={src}
          alt=""
          onLoad={() => setLoadedSrc(src)}
          onError={() => setFailedSrc(src)}
          className={cn(
            'absolute inset-0 size-full object-cover transition-opacity duration-normal',
            loaded ? 'opacity-100' : 'opacity-0',
          )}
        />
      )}
    </span>
  );
}
